import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Mail, 
  Loader2, 
  RefreshCw, 
  KeyRound,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * MFA Verification Modal - Handles OTP input after password login
 */
const MFAVerificationModal = ({ 
  isOpen, 
  onClose, 
  mfaData, // { mfa_token, email_hint, otp_for_testing }
  onSuccess // callback with token and user data
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  
  const inputRefs = useRef([]);

  // Auto-focus first input on open
  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [isOpen]);

  // Countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Show OTP in toast for testing mode
  useEffect(() => {
    if (isOpen && mfaData?.otp_for_testing) {
      toast.info(`[TEST MODE] Your OTP is: ${mfaData.otp_for_testing}`, {
        duration: 30000,
        description: 'This is shown because email is mocked for testing'
      });
    }
  }, [isOpen, mfaData]);

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    
    // Handle paste
    if (value.length > 1) {
      const digits = value.slice(0, 6).split('');
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      // Focus on the next empty input or last input
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
        email: mfaData?.email_hint?.replace(/\*+/g, ''), // This won't work, we use mfa_token
        otp: otpString,
        mfa_token: mfaData?.mfa_token
      });

      toast.success('Verification successful!');
      onSuccess(response.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Verification failed';
      setError(message);
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('mfa_token', mfaData?.mfa_token);

      const response = await axios.post(`${API_URL}/api/auth/resend-otp`, formData);
      
      toast.success('New verification code sent!');
      
      // Show new OTP in test mode
      if (response.data.otp_for_testing) {
        toast.info(`[TEST MODE] New OTP: ${response.data.otp_for_testing}`, {
          duration: 30000
        });
      }

      setCountdown(60); // 60 second cooldown
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      toast.error('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleBackupCodeVerify = async () => {
    if (!backupCode.trim()) {
      setError('Please enter a backup code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('code', backupCode.trim());
      formData.append('mfa_token', mfaData?.mfa_token);

      const response = await axios.post(`${API_URL}/api/auth/mfa/verify-backup-code`, formData);

      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      toast.success('Verification successful!');
      onSuccess(response.data);
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid backup code';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOtp(['', '', '', '', '', '']);
    setError('');
    setShowBackupCode(false);
    setBackupCode('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <Shield className="w-5 h-5 text-teal-500" />
            Two-Factor Authentication
            <Badge className="text-xs bg-teal-500/20 text-teal-600 border-teal-500/30">
              21 CFR Part 11
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            {showBackupCode 
              ? 'Enter one of your backup codes to verify your identity'
              : `Enter the 6-digit code sent to ${mfaData?.email_hint || 'your email'}`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Security Notice */}
          <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
            <p className="text-xs text-teal-600 dark:text-teal-400 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              This verification ensures secure access to clinical trial documents
            </p>
          </div>

          {!showBackupCode ? (
            <>
              {/* OTP Input */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Verification Code
                </Label>
                <div className="flex justify-center gap-2">
                  {otp.map((digit, index) => (
                    <Input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-mono bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 focus:border-teal-500 focus:ring-teal-500"
                    />
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Resend Button */}
              <div className="flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResendOtp}
                  disabled={resending || countdown > 0}
                  className="text-slate-600 dark:text-slate-400 hover:text-teal-600"
                >
                  {resending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {countdown > 0 ? `Resend in ${countdown}s` : "Didn't receive code? Resend"}
                </Button>
              </div>

              {/* Use Backup Code Link */}
              <div className="text-center">
                <button
                  onClick={() => setShowBackupCode(true)}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-teal-600 underline"
                >
                  Use a backup code instead
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Backup Code Input */}
              <div className="space-y-2">
                <Label className="text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Backup Code
                </Label>
                <Input
                  type="text"
                  placeholder="XXXX-XXXX"
                  value={backupCode}
                  onChange={(e) => { setBackupCode(e.target.value.toUpperCase()); setError(''); }}
                  className="font-mono text-center text-lg bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                  Enter one of your 8-character backup codes
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Back to OTP Link */}
              <div className="text-center">
                <button
                  onClick={() => { setShowBackupCode(false); setError(''); }}
                  className="text-xs text-slate-500 dark:text-slate-400 hover:text-teal-600 underline"
                >
                  Use verification code instead
                </button>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1 bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={showBackupCode ? handleBackupCodeVerify : handleVerifyOtp}
            disabled={loading || (!showBackupCode && otp.join('').length !== 6)}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Verify
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MFAVerificationModal;
